import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, LinearProgress, Alert, Grid,
  CircularProgress,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Insights as HorizonIcon,
  Flag as DriverIcon,
  ShowChart as KpiIcon,
  Event as MilestoneIcon,
  TrendingUp as TrendIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { horizonApi, type HorizonResponse } from '../api/services';
import { colourForDriverId } from '../theme/tokens';
import HelpPanel from '../components/HelpPanel';

// ---------------------------------------------------------------------------
// Horizon — quarterly rhythm view (Phase 3 task 3.1). Four sections answer
// "how is this quarter tracking?" in one screen: drivers with progress,
// KPIs against target, upcoming outcomes in the next 90 days, and a four-
// week completion trend. All data flows from a single `GET /api/horizon`
// round trip so the page does not need to orchestrate five fetches.
// ---------------------------------------------------------------------------

function formatDelta(k: HorizonResponse['kpis'][number]): string | null {
  if (k.currentValue === undefined || k.targetValue === undefined) return null;
  const unit = k.unit ?? '';
  return `${k.currentValue}${unit} of ${k.targetValue}${unit}`;
}

export default function HorizonPage() {
  const theme = useTheme();
  const [data, setData] = useState<HorizonResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await horizonApi.get();
        if (!cancelled) setData(response);
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Could not load Horizon.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  const onTrackCount = data.kpis.filter((k) => k.onTrack).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <HorizonIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h4">Horizon</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {data.label} &middot; Q{data.quarter} &middot; {format(parseISO(data.quarterStart), 'd MMM')} to {format(parseISO(data.quarterEnd), 'd MMM yyyy')}
          </Typography>
        </Box>
      </Box>

      <HelpPanel title="About Horizon" storageKey="horizon">
        Horizon is the quarterly rhythm view. It answers "is this quarter still on track?" in one screen: driver progress, KPIs against target, milestones landing in the next ninety days, and how the last four weeks of planned work actually finished.
      </HelpPanel>

      {/* Quarter health summary — a one-line top read before the detail sections. */}
      <Card sx={{ mb: 2, borderLeft: 3, borderColor: 'primary.main' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" fontWeight={600}>
              This quarter at a glance
            </Typography>
            <Chip size="small" label={`${data.drivers.length} active drivers`} />
            <Chip size="small" color={onTrackCount === data.kpis.length ? 'success' : 'warning'} label={`${onTrackCount} of ${data.kpis.length} KPIs on track`} />
            <Chip size="small" label={`${data.upcomingOutcomes.length} milestones in the next 90 days`} />
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {/* Drivers */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <DriverIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="h6">Strategic drivers</Typography>
              </Stack>
              {data.drivers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No active drivers yet. Add an annual driver under Work &rsaquo; Drivers to anchor this quarter's execution.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {data.drivers.map((d) => {
                    const colour = colourForDriverId(d.id);
                    return (
                      <Box key={d.id}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colour }} />
                          <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
                            {d.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {Math.round(d.progressPercentage)}% &middot; {d.kpisOnTrack}/{d.kpiCount} KPIs on track
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, Math.max(0, d.progressPercentage))}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: alpha(colour, 0.15),
                            '& .MuiLinearProgress-bar': { backgroundColor: colour },
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* KPIs */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <KpiIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="h6">KPIs against target</Typography>
              </Stack>
              {data.kpis.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No KPIs defined. Structured KPIs are added on each driver.
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {data.kpis.map((k) => (
                    <Box key={k.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: k.onTrack ? theme.palette.success.main : theme.palette.warning.main }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {k.name}
                          </Typography>
                          {k.driverTitle && (
                            <Typography variant="caption" color="text.secondary">
                              {k.driverTitle}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: k.onTrack ? 'success.main' : 'warning.main', fontWeight: 600 }}>
                          {formatDelta(k) ?? 'Unreported'}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Upcoming outcomes */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <MilestoneIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="h6">Next 90 days</Typography>
              </Stack>
              {data.upcomingOutcomes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No project outcomes dated in the next ninety days. Add target dates on project outcomes to populate this panel.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {data.upcomingOutcomes.map((o) => (
                    <Box key={o.id} sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                      <Typography variant="body2" fontWeight={500}>{o.title}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">{o.projectTitle}</Typography>
                        {o.targetDate && (
                          <Chip size="small" label={format(parseISO(o.targetDate), 'd MMM')} sx={{ height: 18 }} />
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Weekly completion trend */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <TrendIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="h6">Four-week completion trend</Typography>
              </Stack>
              {data.weeklyCompletion.every((w) => w.planned === 0) ? (
                <Typography variant="body2" color="text.secondary">
                  No planned work in the last four weeks to trend on.
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {data.weeklyCompletion.map((w) => {
                    const pct = w.planned === 0 ? 0 : Math.round((w.completed / w.planned) * 100);
                    return (
                      <Box key={w.weekStart}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
                            {format(parseISO(w.weekStart), 'd MMM')} to {format(parseISO(w.weekEnd), 'd MMM')}
                          </Typography>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {w.completed}/{w.planned} items
                          </Typography>
                          <Typography variant="caption" fontWeight={600} sx={{ color: pct >= 80 ? 'success.main' : pct >= 50 ? 'warning.main' : 'error.main' }}>
                            {pct}%
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          color={pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error'}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
